import { useEffect, useRef, useState } from "react";
import { FiHeart, FiMessageSquare, FiEdit2, FiTrash2, FiUser, FiLoader } from "react-icons/fi";
import { feedRequest } from "./api";
import FeedComments from "./FeedComments";
import DeleteFeedPopup from "./DeleteFeedPopup";

export default function FeedPost({ post, revision, onChange }) {
  const [comments, setComments] = useState(false);
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(post.body);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [optimisticLike, setOptimisticLike] = useState(null);
  const [liking, setLiking] = useState(false);
  const likeInFlight = useRef(false);
  const desiredLike = useRef({ liked: post.likedByMe, count: post.likeCount });
  const confirmedLike = useRef({ liked: post.likedByMe, count: post.likeCount });
  const mounted = useRef(true);
  const liked = optimisticLike?.liked ?? post.likedByMe;
  const likeCount = optimisticLike?.count ?? post.likeCount;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    // Keep the optimistic result until the refreshed feed confirms our change.
    if (!liking && optimisticLike && post.likedByMe === optimisticLike.liked) {
      setOptimisticLike(null);
    }
    if (!likeInFlight.current && (!optimisticLike || post.likedByMe === optimisticLike.liked)) {
      desiredLike.current = confirmedLike.current = { liked: post.likedByMe, count: post.likeCount };
    }
  }, [liking, optimisticLike, post.likedByMe, post.likeCount]);

  const toggleLike = async () => {
    if (busy) return;
    const previous = desiredLike.current;
    desiredLike.current = { liked: !previous.liked, count: Math.max(0, previous.count + (previous.liked ? -1 : 1)) };
    setOptimisticLike(desiredLike.current);
    setError("");
    if (likeInFlight.current) return;
    likeInFlight.current = true;
    setLiking(true);
    try {
      // Coalesce clicks while serializing writes; only the latest intent matters.
      while (desiredLike.current.liked !== confirmedLike.current.liked) {
        const target = desiredLike.current.liked;
        await feedRequest(`/posts/${post._id}/like`, { method: target ? "PUT" : "DELETE" });
        confirmedLike.current = { liked: target, count: Math.max(0, confirmedLike.current.count + (target ? 1 : -1)) };
      }
    } catch (error) {
      desiredLike.current = confirmedLike.current;
      if (mounted.current) {
        setOptimisticLike(confirmedLike.current);
        setError(error.message);
      }
    } finally {
      likeInFlight.current = false;
      if (mounted.current) {
        setLiking(false);
      }
      onChange("like-changed");
    }
  };
  const mutate = async (path, options, done = () => {}) => {
    if (busy) return;
    setBusy(true); setError("");
    try { await feedRequest(path, options); done(); onChange(options.method === "DELETE" ? "post-deleted" : "post-edited"); }
    catch (error) { setError(error.message); }
    finally { setBusy(false); }
  };
  return <article className="min-w-0 rounded-lg border border-gray-300 bg-white p-4 shadow-sm sm:p-5" aria-label={`Post by ${post.author?.username || "Deleted user"}`}>
    <div className="flex items-start gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-50 text-orange-700"><FiUser aria-hidden="true" /></span>
      <div className="min-w-0 flex-1"><h3 className="break-words text-sm font-semibold">{post.author?.username || "Deleted user"}</h3><time dateTime={post.createdAt} className="text-xs text-gray-600">{new Date(post.createdAt).toLocaleString()}</time>{post.updatedAt !== post.createdAt && <span className="ml-2 text-xs text-gray-500">Edited</span>}</div>
      <div className="flex shrink-0">
        {post.canEdit && <button disabled={busy} title="Edit post" aria-label="Edit post" onClick={() => { setBody(post.body); setEditing(true); setError(""); }} className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"><FiEdit2 /></button>}
        {post.canDelete && <button disabled={busy} title="Delete post" aria-label="Delete post" onClick={() => { setError(""); setDeleting(true); }} className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-red-50 hover:text-red-700"><FiTrash2 /></button>}
      </div>
    </div>
    {editing ? <form className="mt-4" onSubmit={event => { event.preventDefault(); if (body.trim()) mutate(`/posts/${post._id}`, { method: "PATCH", body: JSON.stringify({ body }) }, () => setEditing(false)); }}>
      <textarea aria-label="Edit post text" rows={4} maxLength={5000} required disabled={busy} value={body} onChange={event => setBody(event.target.value)} className="w-full resize-y rounded-md border border-gray-300 p-3 text-sm focus:border-orange-600 focus:outline-none" />
      <div className="mt-2 flex justify-end gap-3"><button type="button" disabled={busy} onClick={() => setEditing(false)} className="text-sm text-gray-600">Cancel</button><button disabled={busy || !body.trim()} className="h-9 rounded-md bg-orange-700 px-3 text-sm font-semibold text-white disabled:opacity-50">Save changes</button></div>
    </form> : <p className="my-5 whitespace-pre-wrap break-words text-sm font-medium leading-7 text-gray-800">{post.body}</p>}
    {error && !deleting && <p role="alert" className="my-3 text-sm text-red-700">{error}</p>}
    <div className="mt-4 flex flex-wrap items-center gap-5 border-t border-gray-100 pt-3">
      <button aria-label={liked ? "Unlike post" : "Like post"} aria-pressed={liked} aria-busy={liking} disabled={busy} onClick={toggleLike}
        className={`flex min-h-9 items-center gap-2 text-sm font-medium disabled:cursor-wait ${liked ? "text-red-600" : "text-gray-600 hover:text-red-600"}`}><FiHeart aria-hidden="true" className={liked ? "fill-current" : ""} /><span>{likeCount} {likeCount === 1 ? "like" : "likes"}</span></button>
      <button onClick={() => setComments(value => !value)} aria-expanded={comments} className="flex min-h-9 items-center gap-2 text-sm font-medium text-gray-700 hover:text-orange-700"><FiMessageSquare aria-hidden="true" />{post.commentCount} {post.commentCount === 1 ? "comment" : "comments"}</button>
      {busy && <FiLoader aria-label="Updating post" className="animate-spin text-orange-700" />}
    </div>
    {comments && <FeedComments postId={post._id} revision={revision} onChange={onChange} />}
    {deleting && <DeleteFeedPopup kind="post" busy={busy} error={error} onClose={() => setDeleting(false)} onConfirm={() => mutate(`/posts/${post._id}`, { method: "DELETE" }, () => setDeleting(false))} />}
  </article>;
}
